import arc from 'arc';
import { getDistance as haversineDist } from 'ol/sphere';
import { Feature } from 'ol';
import { Geometry, LineString, Point } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import { ParametricRollWarning, RollRiskInfo, RollRiskType, VoyageRouteLineFeature, WeatherWarning } from '../models/voyage-routing-model';
import { CoordinatesUtils } from './coordinates-utils';
import { fromLonLat, toLonLat } from 'ol/proj';
import * as turf from '@turf/turf';
import { buffer, getWidth } from 'ol/extent';
import { Stroke, Style } from 'ol/style';
import '@maersk-global/map-layers/proj/epsg3395';
import { Map as MGMap } from '@maersk-global/map-layers';
import { CustomMapEvents } from '../../local_modules/voyage-map/src/voyage-ol-map';
import { STAR_ROUTE_DEVIATION_AMBER } from '../maplayers/types/plan-layer.types';

// Resolution for great circle interpolation in metres
const GREAT_CIRCLE_RESOLUTION = 200000;

const _PROJECTION = (<any>window)["appenv"].projection;

export function offsetLineCoordinates(
    coordinates: Coordinate[],
    distanceKm: number,
    side: -1 | 1,
    greatCircle: boolean
): Coordinate[] {
    if (coordinates.length < 2 || distanceKm <= 0) return [];

    const normalizeBearing = (bearing: number) => ((bearing % 360) + 360) % 360;
    const getBearing = (from: Coordinate, to: Coordinate) => {
        const fromPoint = turf.point(from);
        const toPoint = turf.point(to);
        return greatCircle
            ? turf.bearing(fromPoint, toPoint)
            : turf.rhumbBearing(fromPoint, toPoint);
    };

    const offsets = coordinates.map((coordinate, index) => {
        const previous = coordinates[index - 1];
        const next = coordinates[index + 1];

        if (!previous) return { bearing: getBearing(coordinate, next!), distance: distanceKm };
        if (!next) return { bearing: normalizeBearing(getBearing(coordinate, previous) + 180), distance: distanceKm };

        // Incoming direction of travel at this vertex, not the bearing back to the previous waypoint.
        const incoming = normalizeBearing(getBearing(coordinate, previous) + 180);
        const outgoing = getBearing(coordinate, next);
        const bearingDelta = ((outgoing - incoming + 540) % 360) - 180;
        // Miter join keeps the full XTD on the inside of a bend; capped for very sharp turns.
        const miter = Math.min(1 / Math.cos((bearingDelta / 2) * Math.PI / 180), 2);
        return { bearing: incoming + bearingDelta / 2, distance: distanceKm * miter };
    });

    return coordinates.map((coordinate, index) => {
        const offsetBearing = normalizeBearing(offsets[index].bearing + side * 90);
        const point = turf.point(coordinate);
        const offsetPoint = greatCircle
            ? turf.destination(point, offsets[index].distance, offsetBearing, { units: 'kilometers' })
            : turf.rhumbDestination(point, offsets[index].distance, offsetBearing, { units: 'kilometers' });
        return offsetPoint.geometry.coordinates;
    });
}

// Run callback function when map is not being moved, i.e. not being animated.
// Graphics can only render on animationFrames, and we get these at the end of animations
export async function handleVectorRerender(map: MGMap, callback: Function, context: any) {
    const callbackPostAnimation = () => {
        callback.apply(context);
        map.removeEventListener(CustomMapEvents.EndOfAnimation, callbackPostAnimation)
    };
    
    if (map.getView().getAnimating()) {
        map.addEventListener(CustomMapEvents.EndOfAnimation, callbackPostAnimation)
    } else {
        callback.apply(context);
    }
}

export function getLegTypeFromDist(p1: [number, number], p2: [number, number])
{
    const MinGCDist = 300 * 1852; // Distances less than 300 nm default to rhumb line

    if (haversineDist(p1, p2) < MinGCDist)
    {
        return "Rhumbline";
    }
    else
    {
        return "GreatCircle";
    }
}

export function isGreatCircleLeg(legType?: string|null): boolean
{
    return legType?.toLowerCase().replace(/[^a-z]/g, '') === 'greatcircle';
}

function datelineSegmentSplit(start: [number, number], end: [number, number]): [number, number][][]
{
    const result: [number, number][][] = [];

    // Short-circuit if the line does not cross the dateline
    // We assume a line crosses the dateline if this is shorter than the alternative
    if (Math.abs(start[0] - end[0]) <= 180)
    {
        return [[start, end]];
    }

    // Start by extracting the coordinates to (x1,y1) and (x2,y2), where (x2,y2) is the point east of the dateline
    const x1 = Math.max(start[0], end[0]);
    const y1 = x1 === start[0] ? start[1] : end[1];
    const x2 = Math.min(start[0], end[0]) + 360;
    const y2 = x1 === start[0] ? end[1] : start[1];

    // Convert to Mercator projection to find the correct intersection point.
    const [mx1, my1] = fromLonLat([x1, y1], _PROJECTION);
    const [mx2, my2] = fromLonLat([x2, y2], _PROJECTION);

    // Determine equation of the line y = mx + b, where m is slope and b is intersect
    // We assume the slope will never be infinite as such cases would return above
    const mslope = (my2 - my1) / (mx2 - mx1);
    const mintersect = my1 - mslope * mx1;

    // Solve for y (latitude) at the dateline
    const mlatAtDateline = mslope * fromLonLat([180, 0])[0] + mintersect;

    // Convert back from Mercator
    const latAtDateline = toLonLat([180, mlatAtDateline], _PROJECTION)[1];

    result.push([start, [Math.sign(start[0]) * 180, latAtDateline]]);
    result.push([[Math.sign(end[0]) * 180, latAtDateline], end]);

    return result;
}

export function handleDateLine(feature: Feature<Geometry>): Feature<Geometry>[]
{
    const features: Feature<Geometry>[] = [];
    (<LineString>feature.getGeometry()).forEachSegment((start, end) => {
        const segments = datelineSegmentSplit(<[number, number]>start, <[number, number]>end);
        for (const segment of segments)
        {
            features.push(new Feature<Geometry>({ geometry: new LineString(segment) }));
        }
    });

    return features;
}

function handleGreatCircle(feature: Feature<Geometry>): Feature<Geometry>[]
{
    const features: Feature<Geometry>[] = [];
    (<LineString>feature.getGeometry()).forEachSegment((start, end) => {
        const geos = generateArcLine(start, end);

        for (const coords of geos)
        {
            const line = new LineString(coords);
            features.push(new Feature<Geometry>({ geometry: line }));
        }
    });

    return features;
}

export function generateArcLine(startCoords: Coordinate, endCoords: Coordinate): Coordinate[][]
{
    // Get the number of segments we want to convert this segment into
    const desiredSegments = Math.floor(haversineDist(startCoords, endCoords) / GREAT_CIRCLE_RESOLUTION) + 1;

    const greatCircleGenerator = new arc.GreatCircle(
        { x: startCoords[0], y: startCoords[1] },
        { x: endCoords[0], y: endCoords[1] }
    )
    const arcLine = greatCircleGenerator.Arc(desiredSegments + 1);

    const geometries: Coordinate[][] = [];
    for (const geometry of arcLine.geometries)
    {
        geometries.push(geometry.coords);
    }

    return geometries;
}

export function CreateDateLineAndGreatCircleFeatures(feature: Feature<Geometry>): Feature<Geometry>[]
{
    if (!(feature.getGeometry() instanceof LineString)) return [feature];

    // We do dateline splitting on rhumblines, great circle interpolation on all other types
    if (!isGreatCircleLeg(feature.getProperties()["leg_type"])) {
        return handleDateLine(feature);
    } else {
        return handleGreatCircle(feature);
    }
}

// Get a LineString that can be used with getCoordinateAt(fraction), handling date line and great circle
export function getLineStringForFractionPositions(feature: VoyageRouteLineFeature): LineString
{
    const startCoords = feature.geometry.coordinates[0];
    let endCoords = feature.geometry.coordinates[1];

    let line: LineString;
    if (feature.properties.leg_type === "Rhumbline")
    {
        // If the line crosses the dateline, adjust the end coordinates
        if (startCoords[0] - endCoords[0] > 180)
        {
            endCoords = [endCoords[0] + 360, endCoords[1]];
        }
        else if (startCoords[0] - endCoords[0] < -180)
        {
            endCoords = [endCoords[0] - 360, endCoords[1]];
        }
        line = new LineString([startCoords, endCoords]);
    }
    else
    {
        // Assume great circle
        const geos = generateArcLine(startCoords, endCoords);
        const coords: [number, number][] = [];
        geos.forEach(geoCoords => {
            geoCoords.forEach(coord => {
                // If the coordinate is on the other side of the dateline compared to the start coordinate, adjust it
                if (startCoords[0] - coord[0] > 180)
                {
                    coord[0] += 360;
                }
                else if (startCoords[0] - coord[0] < -180)
                {
                    coord[0] -= 360;
                }

                coords.push(<[number, number]>coord);
            });
        });
        line = new LineString(coords);
    }

    return line;
}

// Get heading for a point on a (non-transformed) LineString
// Note that the point has to be exactly on the line before this works
export function getHeadingForPoint(coordinates: [number, number], line: LineString): number
{
    const lineCoords = line.getCoordinates();

    if (lineCoords.length === 2)
    {
        // Simple linestring
        return CoordinatesUtils.calcCourse(lineCoords[0], lineCoords[1]);
    }

    // Find the appropriate segment of the LineString and get its heading
    let heading = 0;

    const closestPoint = line.getClosestPoint(coordinates);
    const matchingSegments: [Coordinate, Coordinate][] = [];

    line.forEachSegment((from: Coordinate, to: Coordinate) => {
        const segmentLine = new LineString([from, to]);
        const intersects = segmentLine.intersectsExtent(buffer(new Point(closestPoint).getExtent(), 0.0001));
        if (intersects)
        {
            matchingSegments.push([from, to]);
        }
    });

    if (matchingSegments.length === 1)
    {
        heading = CoordinatesUtils.calcCourse(matchingSegments[0][0], matchingSegments[0][1]);
    }
    else
    {
        // Two segments matched (closestPoint is end point for first, start point for second), find which segment is the correct one
        if (haversineDist(coordinates, matchingSegments[0][0]) < haversineDist(coordinates, matchingSegments[1][1]))
        {
            heading = CoordinatesUtils.calcCourse(matchingSegments[0][0], matchingSegments[0][1]);
        }
        else
        {
            heading = CoordinatesUtils.calcCourse(matchingSegments[1][0], matchingSegments[1][1]);
        }
    }

    return heading;
}

export interface ProcessRouteOptions {
    features: Feature<Geometry>[],
    currentPosition?: [number, number],
    nextWaypointIndex?: number,
    addWarnings?: boolean
}

// Handles pre-processing of a route before plotting on the map
// - splits LineStrings where necessary (for GreatCircle, dateline and styling purposes)
// - adds styleName property to all features (one of "normal", "warning" and "avoid")
// - adds isPassed property if applicable
// Note: Although the function returns a new array, it modifies the features provided
export function processRouteForMap(options: ProcessRouteOptions): Feature<Geometry>[]
{
    const { features, currentPosition, nextWaypointIndex, addWarnings } = options;
    const points = features.filter(feature => feature.getGeometry() instanceof Point);
    const nextWPIndex = nextWaypointIndex !== undefined ? points.indexOf(features[nextWaypointIndex]) : null;

    // Set isPassed on all waypoints before nextWPIndex
    if (nextWPIndex !== null)
    {
        for (let i = 0; i < nextWPIndex; i++)
        {
            points[i].set("isPassed", true);
        }
    }

    let result: Feature<Geometry>[] = [];

    for (let feature of features)
    {
        if (feature.getGeometry() instanceof LineString)
        {
            // Process the line and its points and possibly split the line into multiple features
            const subFeatures = _processAndSplitLine(<Feature<LineString>>feature, points, {nextWpIndex: nextWPIndex, currentPosition: currentPosition, addWarnings: addWarnings});
            result = result.concat(subFeatures);
        }
        else
        {
            result.push(feature);
        }
    }
    return result;
}

interface processAndSplitLineOptions {
    nextWpIndex?: number|null,
    currentPosition?: [number, number],
    addWarnings?: boolean
}

function _processAndSplitLine(lineFeature: Feature<LineString>, allPoints: Feature<Geometry>[], options: processAndSplitLineOptions)
{
    const { nextWpIndex, addWarnings } = options;
    // Split for GreatCircle and/or dateline handling
    const lineStrings = CreateDateLineAndGreatCircleFeatures(lineFeature);
    const lineCoordinates = lineFeature.getGeometry()!.getCoordinates();
    const endPoint = allPoints.find(p => {
        const pcoords = (<Point>p.getGeometry()).getCoordinates();
        return (CoordinatesUtils.isCoordinatesEqual(<Coordinate>(pcoords), lineCoordinates[lineCoordinates.length - 1], 0.000001));
    });

    let legStartCoords: Coordinate | null = null;
    let legStartTime;
    let isPassed = false;
    let isCurrentLeg = false;
    let startPoint: Feature<Geometry> | null = null;

    // Find start point and set timestamp and isPassed (when nextWaypointIndex is supplied)
    for (let i = 0; i < allPoints.length; i++)
    {
        const coordinates = (<Point>allPoints[i].getGeometry()).getCoordinates();
        if (coordinates[0] === lineCoordinates[0][0] && coordinates[1] === lineCoordinates[0][1])
        {
            startPoint = allPoints[i];
            legStartCoords = coordinates;
            legStartTime = allPoints[i].get("timestamp");
            if (nextWpIndex && i >= nextWpIndex)
            {
                // Already past the next waypoint, skip looking for the end point
                break;
            }
        }
        else if (coordinates[0] === lineCoordinates[1][0] && coordinates[1] === lineCoordinates[1][1])
        {
            if (allPoints[i].get("isPassed") || (startPoint && startPoint.get("isPassed") && allPoints[i].get("vesselPositionAt")))
            {
                isPassed = true;
            }
            else if (nextWpIndex !== null && startPoint && startPoint.get("isPassed"))
            {
                isCurrentLeg = true;
            }
            break;
        }
    }

    if (addWarnings)
    {
        splitForWarnings(lineFeature, <Feature<LineString>[]>lineStrings, startPoint!, endPoint!);
    }

    for (const lineString of lineStrings)
    {
        if (!lineString.get("legStartCoords")) lineString.set("legStartCoords", legStartCoords);
        if (!lineString.get("legStartTime")) lineString.set("legStartTime", legStartTime);
        lineString.set("originalFeature", lineFeature);
        lineString.set("leg_type", lineFeature.getProperties()["leg_type"]);
        // Carry the star-route deviation verdict onto each split segment so exceeding legs can be highlighted.
        lineString.set("bpr_deviation_exceeds", lineFeature.getProperties()["bpr_deviation_exceeds"] === true);
        if (isPassed)
        {
            lineString.set("isPassed", isPassed);
        }
    }
    
    return lineStrings;
}

export function splitLineStringForPosition(lines: Feature<Geometry>[], position: Coordinate): void
{
    // Find the relevant section
    let closestLineCoords: Coordinate[];
    let splitLineIndex: number;
    let isGC: boolean = false;

    for (let i = 0; i < lines.length; i++)
    {
        const lineCoords = (<LineString>lines[i].getGeometry()).getCoordinates();
        closestLineCoords = lineCoords;
        splitLineIndex = i;
        isGC = lineCoords.length > 2;

        if (isPointInsideLineSegment(lineCoords[0], lineCoords[lineCoords.length-1], position))
        {
            if (isGC)
            {
                // Also need to find the correct segment inside the line string
                for (let i = 1; i < lineCoords.length; i++)
                {
                    if (isPointInsideLineSegment(lineCoords[i - 1], lineCoords[i], position))
                    {
                        closestLineCoords = [lineCoords[i - 1], lineCoords[i]];
                        break;
                    }
                }
            }
            break;
        }

        // Mark linestring as passed
        lines[i].set("isPassed", true);
    }

    // Calculate where to split
    const projLine: Coordinate[] = [fromLonLat(closestLineCoords![0], _PROJECTION), fromLonLat(closestLineCoords![1], _PROJECTION)];
    const projPoint: Coordinate = fromLonLat(position, _PROJECTION);

    const angleLine = Math.atan2(projLine[1][0] - projLine[0][0], projLine[1][1] - projLine[0][1]);
    const angleToPoint = Math.atan2(projPoint[0] - projLine[0][0], projPoint[1] - projLine[0][1]);
    const angle = Math.abs(angleToPoint - angleLine);
    const h = Math.sqrt(Math.pow(projPoint[0] - projLine[0][0], 2) + Math.pow(projPoint[1] - projLine[0][1], 2));

    const projDistFromStart = h * Math.cos(angle);

    const projLineString = new LineString(closestLineCoords!);
    projLineString.transform('EPSG:4326', _PROJECTION);

    const fraction = projDistFromStart / projLineString.getLength();

    let new1stLine: Feature<Geometry>;
    let new2ndLine: Feature<Geometry>;
    // Split the linestring using the calculated fraction
    if (isGC)
    {
        const intersectionPoint = new LineString(closestLineCoords!).getCoordinateAt(fraction);
        const firstCoords: Coordinate[] = [];
        const secondCoords: Coordinate[] = [intersectionPoint];
        let onFirstLine = true;
        for (const coord of (<LineString>lines[splitLineIndex!].getGeometry()).getCoordinates())
        {
            if (onFirstLine)
            {
                firstCoords.push(coord);
                if (coord[0] === closestLineCoords![0][0] && coord[1] === closestLineCoords![0][1])
                {
                    firstCoords.push(intersectionPoint);
                    onFirstLine = false;
                }
            }
            else
            {
                secondCoords.push(coord);
            }
        }
        new1stLine = new Feature(new LineString(firstCoords));
        new2ndLine = new Feature(new LineString(secondCoords));
    }
    else
    {
        const intersectionPoint = (<LineString>lines[splitLineIndex!].getGeometry()).getCoordinateAt(fraction);
        new1stLine = new Feature(new LineString([closestLineCoords![0], intersectionPoint]));
        new2ndLine = new Feature(new LineString([intersectionPoint, closestLineCoords![1]]));
    }

    new1stLine.set("isPassed", true);
    lines.splice(splitLineIndex!, 1, new1stLine, new2ndLine);
}

function splitForWarnings(lineFeature: Feature<LineString>, lines: Feature<LineString>[], startPoint: Feature<Geometry>, endPoint: Feature<Geometry>): void
{
    const warnSegments: [number, number][][] = [];

    const warnings: VoyageRouteLineFeature["properties"]["warnings"] = lineFeature.get("warnings");
    if (!warnings || (!warnings.parametricRoll && (!warnings.weather || (warnings.weather.warn.length == 0 && warnings.weather.avoid.length === 0))))
    {
        for (const line of lines)
        {
            line.set("warnings", { weather: { warn: [], avoid: [] }, parametricRoll: false });
        }
        return;
    }

    // Handle missing warnings.weather property with present parametric roll warning (VOYC-1515)
    if (!warnings.weather)
    {
        warnings.weather = { warn: [], avoid: [] };
    }

    const lineStringCoords = lineFeature.getGeometry()?.getCoordinates();

    if (!lineStringCoords) {
        return;
    }

    // Collect all segments
    for (const warning of warnings.weather.warn)
    {
        if (warning.segments)
        {
            for (const segment of warning.segments)
            {
                // Leave out segments that span the entire LineString
                if (!CoordinatesUtils.isCoordinatesEqual(segment.coordinates[0], lineStringCoords[0]) ||
                    !CoordinatesUtils.isCoordinatesEqual(segment.coordinates[1], lineStringCoords[1]))
                {
                    warnSegments.push(segment.coordinates);
                }
            }
        }
    }

    for (const warning of warnings.weather.avoid)
    {
        if (warning.segments)
        {
            for (const segment of warning.segments)
            {
                // Leave out segments that span the entire LineString
                if (!CoordinatesUtils.isCoordinatesEqual(segment.coordinates[0], lineStringCoords[0]) ||
                    !CoordinatesUtils.isCoordinatesEqual(segment.coordinates[1], lineStringCoords[1]))
                {
                    warnSegments.push(segment.coordinates);
                }
            }
        }
    }
    if (warnings.parametricRoll)
    {
        for (const segment of warnings.parametricRoll)
        {
            // Leave out segments that span the entire LineString
            if (!CoordinatesUtils.isCoordinatesEqual(segment.coordinates[0], lineStringCoords[0]) ||
                !CoordinatesUtils.isCoordinatesEqual(segment.coordinates[1], lineStringCoords[1]))
            {
                warnSegments.push(segment.coordinates);
            }
        }
    }

    // Get splitting points (sorted and without duplicates)
    const points: [number, number][] = [];
    for (const segment of warnSegments)
    {
        for (const point of segment)
        {
            // Leave out start/end point
            if (!(CoordinatesUtils.isCoordinatesEqual(point, lineStringCoords[0]) ||
                CoordinatesUtils.isCoordinatesEqual(point, lineStringCoords[1])))
            {
                points.push(point);
            }
        }
    }

    // Sort based on distance to line start point
    const isRhumbline = lineFeature.getProperties()["leg_type"] === 'Rhumbline';
    const startPt = lineStringCoords[0];
    points.sort((a, b) => {
        if (a[0] === b[0] && a[1] === b[1]) 
        {
            return 0;
        }
        const distA = isRhumbline ? CoordinatesUtils.calcRhumblineDist(startPt, a) : haversineDist(startPt, a);
        const distB = isRhumbline ? CoordinatesUtils.calcRhumblineDist(startPt, b) : haversineDist(startPt, b);

        return distA - distB;
    });

    // Remove any duplicates
    for (let i = 0; i < points.length - 1; i++)
    {
        while (i < points.length - 1 && CoordinatesUtils.isCoordinatesEqual(points[i], points[i + 1]))
        {
            points.splice(i + 1, 1);
        }
    }
    // Split lines further at the splitting points
    for (const point of points)
    {
        // Find line to split
        for (let i = 0; i < lines.length; i++)
        {
            const coords = lines[i].getGeometry()?.getCoordinates();

            if (!coords) {
                continue;
            }

            const start = coords[0];
            const end = coords[coords.length - 1];
            if (isPointInsideLineSegment(start, end, point))
            {
                // Split line at point
                const firstCoords: Coordinate[] = [];
                const secondCoords: Coordinate[] = [point];
                let onFirstLine = true;
                for (let j = 0; j < coords.length; j++)
                {
                    if (onFirstLine && (j === coords.length - 1 || !isPointInsideLineSegment(coords[j], end, point)))
                    {
                        onFirstLine = false;
                        firstCoords.push(point);
                    }

                    if (onFirstLine)
                    {
                        firstCoords.push(coords[j]);
                    }
                    else
                    {
                        secondCoords.push(coords[j]);
                    }
                }
                const new1stLine = <Feature<LineString>>new Feature(new LineString(firstCoords));
                const new2ndLine = <Feature<LineString>>new Feature(new LineString(secondCoords));

                if (lines[i].get("isPassed"))
                {
                    new1stLine.set("isPassed", true);
                    new2ndLine.set("isPassed", true);
                }
                lines.splice(i, 1, new1stLine, new2ndLine)
                break;
            }
        }
    }

    const startTime = new Date(startPoint?.get("timestamp")).getTime();
    const endTime = new Date(endPoint?.get("timestamp")).getTime();
    const endPtCoords = (<Point>endPoint.getGeometry()).getCoordinates();

    const totalLineLength = isRhumbline ? CoordinatesUtils.calcRhumblineDist(startPt, endPtCoords) : haversineDist(startPt, endPtCoords) / 1852;
    const timeSpan = endTime - startTime;

    // Assign warnings and leg details to the resulting linestrings
    for (const line of lines)
    {
        const coords = line.getGeometry()?.getCoordinates()[0];
        const dist = isRhumbline ? CoordinatesUtils.calcRhumblineDist(startPt, coords!) : haversineDist(startPt, coords!) / 1852;
        
        if (startPoint?.get("timestamp") && endPoint?.get("timestamp")) {
            try {
                const time = new Date(startTime + (dist / totalLineLength) * timeSpan).toISOString();
                line.set("legStartTime", time);
            }

            catch (error) {
                console.error("Error converting to ISO string:", error);
            }
        }

        line.set("warnings", { weather: { warn: [], avoid: [] }, parametricRoll: false });
        line.set("legStartCoords", coords);
        
    }

    for (const avoid of warnings.weather.avoid)
    {
        applyWarnAvoidToLines(avoid, lines, lineStringCoords, true);
    }
    for (const warn of warnings.weather.warn)
    {
        applyWarnAvoidToLines(warn, lines, lineStringCoords, false);
    }

    if (warnings.parametricRoll && warnings.parametricRoll.length > 0)
    {
        applyParametricRollToLines(warnings.parametricRoll, lines);
    }

    return;
}

function applyWarnAvoidToLines(warning: WeatherWarning, lines: Feature<LineString>[], legCoords: Coordinate[], isAvoid: boolean): void
{
    let segments = warning.segments;
    if (!segments)
    {
        segments = [{
            actual: warning.actual!,
            coordinates: <[number, number][]>legCoords
        }];
    }

    for (const segment of segments)
    {
        let warningStarted = false;
        for (const line of lines)
        {
            if (!warningStarted && CoordinatesUtils.isCoordinatesEqual(segment.coordinates[0], line.getGeometry()!.getCoordinates()[0]))
            {
                warningStarted = true;
            }

            if (warningStarted)
            {
                if (CoordinatesUtils.isCoordinatesEqual(segment.coordinates[1], line.getGeometry()!.getCoordinates()[0]))
                {
                    // Warning has ended
                    break;
                }

                const lineWarnings = line.get("warnings");
                if (isAvoid)
                {
                    lineWarnings!.weather.avoid.push({
                        type: warning.type,
                        limit: warning.limit,
                        actual: segment.actual,
                    });
                }
                else
                {
                    lineWarnings!.weather.warn.push({
                        type: warning.type,
                        limit: warning.limit,
                        actual: segment.actual,
                    });
                }
            }
        }
    }
}

function applyParametricRollToLines(warning: ParametricRollWarning[], lines: Feature<LineString>[])
{
    for (const segment of warning)
    {
        let warningStarted = false;
        for (let i = 0; i < lines.length; i++)
        {
            const line = lines[i];
            const lineCoords = line.getGeometry()!.getCoordinates();
            const lineStart = lineCoords[0];
            const lineEnd = lineCoords[lineCoords.length - 1];
            
            if (!warningStarted && CoordinatesUtils.isCoordinatesEqual(segment.coordinates[0], lineStart))
            {
                warningStarted = true;
            }

            if (warningStarted)
            {
                const lineWarnings = line.get("warnings");
                lineWarnings!.parametricRoll = true;
                const parametricStartTime = segment.timeStart;
                const parametricEndTime = segment.timeEnd;

                const startTime = new Date(parametricStartTime).getTime(); // in ms
                const endTime = new Date(parametricEndTime).getTime();     // in ms

                const durationMs = endTime - startTime;
                const durationMinutes = Math.round(durationMs / 1000 / 60);
                line.set("parametricDuration", durationMinutes);
                for (const rollRiskType of getDisplayRollRiskTypes(segment))
                {
                    addRollRiskType(line, rollRiskType);
                }
                if (CoordinatesUtils.isCoordinatesEqual(segment.coordinates[1], lineEnd))
                {
                    break;
                }
            }
        }
    }
}

function normalizeRollRiskInfo(rollRiskInfo?: RollRiskInfo|RollRiskInfo[]): RollRiskInfo[]
{
    if (!rollRiskInfo)
    {
        return [];
    }

    return Array.isArray(rollRiskInfo) ? rollRiskInfo : [rollRiskInfo];
}

function hasRollRiskType(segment: ParametricRollWarning, riskType: RollRiskType): boolean
{
    return normalizeRollRiskInfo(segment.roll_risk_info).some(info => info.risk_type === riskType);
}

function getDisplayRollRiskTypes(segment: ParametricRollWarning): RollRiskType[]
{
    return normalizeRollRiskInfo(segment.roll_risk_info)
        .map(info => info.risk_type)
        .filter((riskType, index, riskTypes) => riskTypes.indexOf(riskType) === index);
}

function addRollRiskType(line: Feature<LineString>, rollRiskType: RollRiskType): void
{
    const riskTypes = <RollRiskType[]>(line.get("rollRiskTypes") || []);
    if (!riskTypes.includes(rollRiskType)) line.set("rollRiskTypes", [...riskTypes, rollRiskType]);

    // Preserve the legacy singular property for consumers that have not migrated yet.
    const existingRiskType = <RollRiskType|undefined>line.get("rollRiskType");
    if (existingRiskType === "parametric_roll_risk")
    {
        return;
    }

    if (rollRiskType === "parametric_roll_risk" || !existingRiskType)
    {
        line.set("rollRiskType", rollRiskType);
    }
}

// Returns true if a given point is inside a (RL/GC) line segment when projected on to the line
// Point does not have to be on the line
function isPointInsideLineSegment(start: Coordinate, end: Coordinate, position: Coordinate)
{
    // Check if the point is inside this linesegment by checking the relative angles
    const startToPointHeading = CoordinatesUtils.calcCourse(start, position);
    const lineHeading = CoordinatesUtils.calcCourse(start, end);
    const endToPointHeading = CoordinatesUtils.calcCourse(end, position);
    const revLineHeading = CoordinatesUtils.calcCourse(end, start);
    return Math.abs(startToPointHeading - lineHeading) <= 90 && Math.abs(endToPointHeading - revLineHeading) <= 90;
};

interface RouteLineColors {
    defaultColor: string;
    waypointFillColor: string;
    passedColor: string;
    warningColor: string;
    avoidColor: string;
    parametricRollColor: string;
    heavyRollingColor: string;
    transverseAccelerationColor: string;
    uiRouteColor?: string;
}

export function routeLineStyles(lineString: Feature<LineString>, resolution: number, colors: RouteLineColors)
{
    const styles = [
        new Style({
            stroke: new Stroke({
                color: colors.uiRouteColor ?  colors.uiRouteColor : colors.defaultColor,
                width: 6
            })
        })
    ]

    if (lineString.get("isPassed"))
    {
        lineString.setStyle(new Style({
            stroke: new Stroke({
                color: colors.passedColor, width: 6
            })
        }));
    }
    else
    {
        const warnings = lineString.get("warnings");

        const lineCoords = (<LineString>lineString.getGeometry()).getCoordinates();
        const from = [lineCoords[0][0], lineCoords[0][1]];
        const to = [lineCoords[1][0], lineCoords[1][1]];

        const dx = from[0] - to[0];
        const dy = from[1] - to[1]

        const angle = Math.atan2(dy, dx);

        const params = {
            resolution: resolution,
            angle: angle,
            coords: {from: from, to: to}
        }

        if (warnings.weather.avoid.length > 0)
        {
            const warningCoords = parallelLineCoords(-4, params);
            styles.push(new Style({
                geometry: new LineString(warningCoords),
                stroke: new Stroke({
                    color: colors.avoidColor,
                    width: 6,
                    lineDash: [6],
                    lineDashOffset: 6,
                    lineCap: "butt"
                })
            }))
        }

        if (warnings.weather.warn.length > 0 && warnings.weather.avoid.length === 0)
        {
            const warningCoords = parallelLineCoords(-4, params);
            styles.push(new Style({
                geometry: new LineString(warningCoords),
                stroke: new Stroke({
                    color: colors.warningColor,
                    width: 6,
                    lineDash: [6],
                    lineDashOffset: 6,
                    lineCap: "butt"
                })
            }))
        }

        if (warnings.parametricRoll)
        {
            const lineCoords = (<LineString>lineString.getGeometry()).getCoordinates();
            const legacyRiskType = <RollRiskType|undefined>lineString.get("rollRiskType");
            const rollRiskTypes = <RollRiskType[]>(lineString.get("rollRiskTypes") || (legacyRiskType ? [legacyRiskType] : []));
            const displayedRiskTypes = rollRiskTypes.length > 0 ? rollRiskTypes : ["parametric_roll_risk" as RollRiskType];
            const colorByRiskType: Record<RollRiskType, string> = {
                parametric_roll_risk: colors.parametricRollColor,
                high_roll_risk: colors.heavyRollingColor,
                transverse_acceleration_risk: colors.transverseAccelerationColor,
            };

            displayedRiskTypes.forEach((riskType, riskIndex) => {
                const offsetWarningCoords: number[][] = [];
                for (let i = 0; i < lineCoords.length - 1; i++) {
                    const from = [lineCoords[i][0], lineCoords[i][1]];
                    const to = [lineCoords[i + 1][0], lineCoords[i + 1][1]];
                    const params = {
                        resolution,
                        angle: Math.atan2(from[1] - to[1], from[0] - to[0]),
                        coords: { from, to },
                    };
                    const segmentCoords = parallelLineCoords(4 + riskIndex * 6, params);
                    if (i === 0) offsetWarningCoords.push(segmentCoords[0]);
                    offsetWarningCoords.push(segmentCoords[1]);
                }
                styles.push(new Style({
                    geometry: new LineString(offsetWarningCoords),
                    stroke: new Stroke({
                        color: colorByRiskType[riskType],
                        width: 6,
                        lineDash: [12, 6],
                        lineDashOffset: 6,
                        lineCap: "butt"
                    })
                }));
            });
        }
    }

    // Highlight legs that exceed the allowed deviation from the top-1 star route (env-gated).
    const deviationEnabled = (<any>window)?.["appenv"]?.featureToggles?.starRouteDeviation === true;
    if (deviationEnabled && lineString.get("bpr_deviation_exceeds") === true)
    {
        styles.push(new Style({
            stroke: new Stroke({
                color: STAR_ROUTE_DEVIATION_AMBER,
                width: 6,
                lineDash: [10, 10],
            })
        }));
    }

    return styles
}

interface parallelLineParams{
    resolution: number,
    angle: number,
    coords: {from: number[], to: number[]}
}

function parallelLineCoords(distToLineInPixels: number, params: parallelLineParams): number[][]
{
    // distance between warning and leg
    const dist = distToLineInPixels * params.resolution;
    // use angle to make the line parallel to the leg
    const newFrom = [
        Math.sin(params.angle) * dist + params.coords.from[0],
        -Math.cos(params.angle) * dist + params.coords.from[1]
    ];
    const newTo = [
        Math.sin(params.angle) * dist + params.coords.to[0],
        -Math.cos(params.angle) * dist + params.coords.to[1]
    ]

    return [newFrom, newTo]
}

export const getFeatureCenterPosition = (feature: Feature<Point>, map: MGMap): number[] => {

    if (!feature) {
        throw new Error("getFeatureCenterPosition: missing argument feature")
    }

    if (!map) {
        throw new Error("getFeatureCenterPosition: missing argument map")
    }

    const worldWidth = getWidth(map!.getView().getProjection().getExtent());
    const currentWorld = Math.round(map!.getView()!.getCenter()![0] / worldWidth);

    const clone = feature.getGeometry()?.clone()
    clone!.translate(currentWorld * worldWidth, 0);

    const coordinates = clone?.getCoordinates()

    if (!coordinates) {
        throw new Error("getFeatureCenterPosition: failed to get cordinates")
    }

    return coordinates
}
